<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="modelValue"
        class="base-popup-overlay"
        :style="overlayStyle"
      />
    </Transition>
    <Transition name="transform">
      <div
        v-if="modelValue"
        class="base-popup"
        :class="{ 'base-popup--fit-viewport-on-mobile': props.fullHeightOnMobile }"
        :style="popupStyle"
        @click.self="!props.persistent && (modelValue = false)"
      >
        <div
          class="base-popup__wrapper"
          :class="[
            `base-popup__wrapper--variant-${props.variant}`,
            { 'base-popup__wrapper--fit-viewport-on-mobile': props.fullHeightOnMobile },
            { 'base-popup__wrapper--allow-content-overflow': props.allowContentOverflow },
          ]"
          :style="
            props.withTransparent
              ? { boxShadow: 'none', backgroundColor: 'rgba(0,0,0,0)' }
              : {}
          "
        >
          <div v-if="props.withClose" class="base-popup__close">
            <BaseButtonIcon
              icon-name="vuesax:close-linear"
              icon-size="20px"
              :variant="props.closeVariant"
              @click="handleClose"
            />
          </div>
          <slot>
            <BasePopupContent>
              <template v-if="$slots.title" #title>
                <slot name="title" />
              </template>
              <template v-if="$slots.body" #body>
                <slot name="body" v-bind="{ handleClose }" />
              </template>
              <template
                v-if="$slots.actions"
                #actions="{ rowStyle, columnStyle }"
              >
                <slot name="actions" v-bind="{ rowStyle, columnStyle, handleClose }" />
              </template>
            </BasePopupContent>
          </slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import type { PopupStackRegistration } from './popupStack'
import BaseButtonIcon from '../button-icon/index.vue'
import BasePopupContent from '../popup/PopupContent.vue'
import {
  isTopPopupStackLayer,

  registerPopupStackLayer,
} from './popupStack'

interface Props {
  withClose?: boolean
  withTransparent?: boolean
  maxWidth?: string
  minHeight?: string
  maxHeight?: string
  variant?: 'secondary' | 'light'
  closeVariant?: 'primary' | 'secondary' | 'light'
  /** При true popup нельзя закрыть кликом по overlay и Escape */
  persistent?: boolean
  /** На мобильных экранах ограничивает высоту по viewport, без скролла. Padding сохраняется. */
  fullHeightOnMobile?: boolean
  /**
   * У внутренней обёртки `overflow: visible`, чтобы выпадающие списки (select, time-picker и т.п.)
   * не обрезались. Скролл длинного контента остаётся на внешнем `.base-popup`.
   */
  allowContentOverflow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'light',
  withClose: true,
  persistent: false,
  allowContentOverflow: false,
})
const modelValue = defineModel<boolean>()

const popupStackRegistration = ref<PopupStackRegistration | null>(null)

const computedStyle = computed(() => ({
  ...(props.maxWidth ? { '--popup-max-width': props.maxWidth } : {}),
  ...(props.minHeight ? { '--popup-min-height': props.minHeight } : {}),
  ...(props.maxHeight ? { '--popup-max-height': props.maxHeight } : {}),
}))

const overlayStyle = computed(() => {
  if (!popupStackRegistration.value) {
    return undefined
  }

  return {
    zIndex: popupStackRegistration.value.overlayZIndex,
  }
})

const popupStyle = computed(() => {
  if (!popupStackRegistration.value) {
    return computedStyle.value
  }

  return {
    ...computedStyle.value,
    zIndex: popupStackRegistration.value.popupZIndex,
  }
})

watch(
  modelValue,
  (isOpen) => {
    if (isOpen) {
      if (popupStackRegistration.value) {
        popupStackRegistration.value.release()
      }
      popupStackRegistration.value = registerPopupStackLayer()
      return
    }

    popupStackRegistration.value?.release()
    popupStackRegistration.value = null
  },
  { immediate: true },
)

onMounted(() => {
  document.addEventListener('keydown', handleEsc)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEsc)
  popupStackRegistration.value?.release()
  popupStackRegistration.value = null
})

function handleEsc(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !modelValue.value || props.persistent) {
    return
  }

  if (!isTopPopupStackLayer(popupStackRegistration.value?.id ?? null)) {
    return
  }

  modelValue.value = false
}

function handleClose() {
  if (props.withClose) {
    modelValue.value = false
  }
}
</script>

<style lang="scss" scoped>
.base-popup-overlay {
  background-color: #fff3;
  backdrop-filter: blur(16px);
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  max-height: 100dvh;
}

.base-popup {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  max-height: 100dvh;
  overflow: hidden auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: rem(80px) rem(16px);

  &__wrapper {
    color: var(--color-foreground-regular);
    position: relative;
    min-width: 340px;
    max-width: var(--popup-max-width, 674px);
    min-height: var(--popup-min-height, auto);
    max-height: var(--popup-max-height, none);
    height: var(--popup-max-height, auto);
    width: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 24px;
    overflow: auto;
    box-shadow: 0 0 13.8px 0 #00000040;
    margin: auto;

    &--allow-content-overflow {
      overflow: visible;
    }

    &--variant {
      &-secondary {
        background-color: var(--color-surface-placeholder);
      }

      &-light {
        background-color: var(--color-surface-bg);
      }
    }

    &--fit-viewport-on-mobile {
      @include bp-media('xs', 'sm') {
        min-height: calc(100dvh - #{rem(160px)});
        max-height: calc(100dvh - #{rem(160px)});
        height: calc(100dvh - #{rem(160px)});
        overflow: hidden;
      }
    }
  }

  &__close {
    position: absolute;
    z-index: 1;
    top: rem(20px);
    right: rem(20px);

    @include bp-media('xs', 'sm') {
      top: rem(12px);
      right: rem(12px);
    }

    &:deep(button) {
      @include bp-media('xs', 'sm') {
        height: 32px;
        width: 32px;
      }
    }
  }
}

.base-popup--fit-viewport-on-mobile {
  @include bp-media('xs', 'sm') {
    padding: rem(80px) rem(16px);
  }
}

.fade-enter-active {
  transition: opacity 0.3s ease-in;
}

.fade-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.transform-enter-active,
.transform-leave-active {
  transition:
    opacity 0.3s ease-in-out,
    transform 0.3s ease-in-out;
}

.transform-enter-from,
.transform-leave-to {
  opacity: 0;
  transform: scale(0.8);
}
</style>
